import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, AudioLines, Bot, Check, Globe2,
  Languages, MessageCircle, ShieldCheck, Sparkles, Star, Zap
} from 'lucide-react';
import {
  blogAPI, botSettingsAPI, faqAPI, featuresAPI, heroAPI,
  partnerAPI, statisticsAPI, tariffsAPI, testimonialsAPI
} from '../utils/api';
import { trackBotClick } from '../components/AnalyticsTracker';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOMetadata from '../components/SEOMetadata';

const fallbackFeatures = [
  { id: 'voice', title: 'Голос и текст', description: 'Переводите сообщения и голосовые без переключения между приложениями.', icon: 'audio' },
  { id: 'context', title: 'Понимает контекст', description: 'AI учитывает смысл диалога, стиль общения и естественные формулировки.', icon: 'sparkles' },
  { id: 'languages', title: 'Много языков', description: 'Общайтесь с людьми по всему миру прямо в привычном Telegram-чате.', icon: 'globe' },
  { id: 'fast', title: 'Ответ за секунды', description: 'Отправьте сообщение боту и почти сразу получите готовый перевод.', icon: 'zap' },
  { id: 'private', title: 'Приватность', description: 'Доступ и операции проходят через защищённый серверный контур.', icon: 'shield' },
  { id: 'simple', title: 'Без установки', description: 'Никаких новых приложений: достаточно открыть @lngaua_bot.', icon: 'bot' }
];

const fallbackStats = [
  { id: 'messages', value: '3 000+', label: 'переведённых сообщений' },
  { id: 'users', value: '680+', label: 'пользователей' },
  { id: 'start', value: '15', label: 'сообщений бесплатно' },
  { id: 'access', value: '24/7', label: 'доступен в Telegram' }
];

const iconMap = {
  audio: AudioLines, sparkles: Sparkles, globe: Globe2,
  zap: Zap, shield: ShieldCheck, bot: Bot
};

const reveal = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6 }
};

const HomePage = () => {
  const [data, setData] = useState({
    hero: null, tariffs: [], features: [], testimonials: [], faq: [],
    statistics: [], partner: null, posts: [], bot: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      heroAPI.get().catch(() => ({ data: {} })),
      tariffsAPI.getAll().catch(() => ({ data: [] })),
      featuresAPI.getAll().catch(() => ({ data: [] })),
      testimonialsAPI.getAll().catch(() => ({ data: [] })),
      faqAPI.getAll().catch(() => ({ data: [] })),
      statisticsAPI.getAll().catch(() => ({ data: [] })),
      partnerAPI.get().catch(() => ({ data: null })),
      blogAPI.getAll().catch(() => ({ data: [] })),
      botSettingsAPI.get().catch(() => ({ data: {} }))
    ]).then(([hero, tariffs, features, testimonials, faq, statistics, partner, posts, bot]) => {
      setData({
        hero: hero.data,
        tariffs: Array.isArray(tariffs.data) ? tariffs.data : [],
        features: Array.isArray(features.data) ? features.data : [],
        testimonials: Array.isArray(testimonials.data) ? testimonials.data : [],
        faq: Array.isArray(faq.data) ? faq.data : [],
        statistics: Array.isArray(statistics.data) ? statistics.data : [],
        partner: partner.data,
        posts: Array.isArray(posts.data) ? posts.data.slice(0, 3) : [],
        bot: bot.data || {}
      });
    }).finally(() => setLoading(false));
  }, []);

  const openBot = async () => {
    const url = data.bot.telegram_url || data.hero?.bot_link || 'https://t.me/lngaua_bot';
    await trackBotClick('telegram');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const features = data.features.length ? data.features : fallbackFeatures;
  const stats = data.statistics.length ? data.statistics : fallbackStats;
  const title = data.hero?.title || 'Перевод, который звучит естественно';
  const subtitle = data.hero?.subtitle || 'AI-переводчик для живого общения';
  const description = data.hero?.description || 'Пишите и говорите на своём языке — LNG AUA поможет понять друг друга прямо в Telegram.';

  return (
    <div className="public-page nocturne-page">
      <SEOMetadata
        title="AI-переводчик текста и голоса"
        description="Естественный перевод текста и голосовых сообщений прямо в Telegram. 15 сообщений бесплатно."
        keywords="AI переводчик, перевод голоса, телеграм бот, переводчик текста"
        path="/"
      />
      <Header />

      <main>
        <section className="nocturne-hero">
          <div className="nocturne-hero-glow glow-one" />
          <div className="nocturne-hero-glow glow-two" />
          <motion.div className="nocturne-hero-copy" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="nocturne-eyebrow"><Sparkles size={14} /> {subtitle}</span>
            <h1>{title.split(' ').slice(0, -1).join(' ')} <em>{title.split(' ').slice(-1)}</em></h1>
            <p>{description}</p>
            <div className="nocturne-actions">
              <button className="nocturne-button primary large" onClick={openBot}>
                {data.hero?.cta_button_text || 'Попробовать бесплатно'} <ArrowUpRight size={18} />
              </button>
              <a className="nocturne-button ghost large" href="#how-it-works">Как это работает <ArrowRight size={18} /></a>
            </div>
            <div className="nocturne-trust">
              <span className="trust-avatars"><i>EN</i><i>DE</i><i>ES</i></span>
              <span><strong>15 сообщений</strong> для старта без оплаты</span>
            </div>
          </motion.div>

          <motion.div className="nocturne-hero-visual" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1 }}>
            <div className="nocturne-image-wrap">
              <img src="/concept-lab-a7f39c/assets/hero-midnight.png" alt="Общение без языковых границ" />
              <span className="image-sheen" />
            </div>
            <div className="nocturne-float-card card-a"><small>Определён язык</small><b>Español</b><span>Точность контекста</span></div>
            <div className="nocturne-float-pill"><span /> AI online</div>
            <div className="nocturne-float-card card-b"><small>Перевод</small><b>1.2 сек</b><span>Готов к отправке</span></div>
          </motion.div>
        </section>

        <section className="nocturne-metrics" aria-label="LNG AUA в цифрах">
          {stats.slice(0, 4).map((stat) => (
            <div key={stat.id || stat.label}><strong>{stat.value}</strong><span>{stat.label}</span></div>
          ))}
        </section>

        <section className="nocturne-section" id="features">
          <motion.div className="nocturne-section-heading" {...reveal}>
            <span>Всё необходимое</span>
            <h2>Общение без языкового барьера</h2>
            <p>Не отдельный сложный сервис, а понятный помощник в мессенджере, которым вы уже пользуетесь.</p>
          </motion.div>
          <div className="nocturne-feature-grid">
            {features.slice(0, 6).map((feature, index) => {
              const Icon = iconMap[feature.icon] || [Languages, Sparkles, Globe2, Zap, ShieldCheck, Bot][index % 6];
              return (
                <motion.article key={feature.id || feature.title} className={`nocturne-card ${index === 0 ? 'wide' : ''}`} {...reveal} transition={{ duration: 0.5, delay: index * 0.05 }}>
                  <span className="nocturne-icon"><Icon /></span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.article>
              );
            })}
          </div>
        </section>

        <section className="nocturne-section nocturne-steps" id="how-it-works">
          <motion.div className="nocturne-section-heading centered" {...reveal}>
            <span>Три простых шага</span>
            <h2>Начните за минуту</h2>
          </motion.div>
          <div className="nocturne-step-grid">
            {[
              ['01', 'Откройте бота', 'Перейдите в @lngaua_bot и нажмите «Старт».'],
              ['02', 'Отправьте сообщение', 'Введите текст или запишите голосовое на своём языке.'],
              ['03', 'Получите перевод', 'Бот определит язык и вернёт естественный перевод.']
            ].map(([number, heading, copy]) => (
              <motion.article key={number} {...reveal}>
                <span>{number}</span><h3>{heading}</h3><p>{copy}</p>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="nocturne-section">
          <motion.div className="nocturne-section-heading" {...reveal}>
            <span>Гибкая оплата</span>
            <h2>Платите только за нужный объём</h2>
            <p>Начните с бесплатного лимита, затем выберите пакет сообщений без сложной подписки.</p>
          </motion.div>
          {data.tariffs.length ? (
            <div className="nocturne-pricing-grid">
              {data.tariffs.slice(0, 3).map((tariff) => (
                <article key={tariff.id} className={tariff.is_popular ? 'popular' : ''}>
                  {tariff.is_popular && <span className="popular-label">Популярный</span>}
                  <h3>{tariff.name}</h3>
                  <strong>{tariff.price} ₽</strong>
                  <p>{tariff.messages_count} сообщений</p>
                  {(tariff.features || []).map((item) => <span className="price-feature" key={item}><Check size={15} /> {item}</span>)}
                  <button className="nocturne-button primary" onClick={openBot}>Выбрать в боте <ArrowUpRight size={16} /></button>
                </article>
              ))}
            </div>
          ) : (
            <div className="nocturne-empty-offer">
              <div><span>Стартовый доступ</span><strong>15 сообщений бесплатно</strong><p>Актуальные пакеты и стоимость всегда доступны внутри Telegram-бота.</p></div>
              <button className="nocturne-button primary large" onClick={openBot}>Посмотреть тарифы <ArrowUpRight size={18} /></button>
            </div>
          )}
          <Link className="nocturne-text-link" to="/tariffs">Подробнее о тарифах <ArrowRight size={16} /></Link>
        </section>

        {data.testimonials.length > 0 && (
          <section className="nocturne-section">
            <div className="nocturne-section-heading"><span>Отзывы</span><h2>Что говорят пользователи</h2></div>
            <div className="nocturne-review-grid">
              {data.testimonials.slice(0, 3).map((item) => (
                <article key={item.id} className="nocturne-card">
                  <div className="review-stars">{Array.from({ length: item.rating || 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</div>
                  <p>«{item.text}»</p><strong>{item.name}</strong><small>{item.company}</small>
                </article>
              ))}
            </div>
          </section>
        )}

        {data.partner && (
          <section className="nocturne-story">
            <motion.div {...reveal}>
              <span className="nocturne-eyebrow"><MessageCircle size={14} /> Партнёрская программа</span>
              <h2>{data.partner.title}</h2><p>{data.partner.description}</p>
              <Link className="nocturne-button ghost large" to="/partner-program">Узнать условия <ArrowRight size={18} /></Link>
            </motion.div>
            <div className="partner-numbers"><div><strong>{data.partner.level1_percent}%</strong><span>первый уровень</span></div><div><strong>{data.partner.level2_percent}%</strong><span>второй уровень</span></div></div>
          </section>
        )}

        {data.posts.length > 0 && (
          <section className="nocturne-section">
            <div className="nocturne-section-heading"><span>Полезные материалы</span><h2>Новое в блоге</h2></div>
            <div className="nocturne-blog-grid">
              {data.posts.map((post) => <Link key={post.id} to={`/blog/${post.id}`} className="nocturne-card"><small>{new Date(post.created_at).toLocaleDateString('ru-RU')}</small><h3>{post.title}</h3><p>{post.excerpt}</p><span>Читать <ArrowRight size={15} /></span></Link>)}
            </div>
          </section>
        )}

        {data.faq.length > 0 && (
          <section className="nocturne-section nocturne-faq">
            <div className="nocturne-section-heading"><span>FAQ</span><h2>Частые вопросы</h2></div>
            <div>{data.faq.slice(0, 6).map((item) => <details key={item.id}><summary>{item.question}<span>+</span></summary><p>{item.answer}</p></details>)}</div>
          </section>
        )}

        <section className="nocturne-cta">
          <div><span>Пора попробовать</span><h2>Ваш первый перевод — уже сейчас</h2><p>Откройте Telegram и получите 15 бесплатных сообщений.</p></div>
          <button className="nocturne-button dark large" onClick={openBot}>Открыть @lngaua_bot <ArrowUpRight size={18} /></button>
        </section>
      </main>
      <Footer />
      {loading && <div className="nocturne-loading" aria-live="polite">Обновляем данные…</div>}
    </div>
  );
};

export default HomePage;
